/**
 * Class-name recipes shared by kit components and by call sites that need the
 * kit look on a non-kit element (e.g. a Link styled as a button). They live
 * apart from the component files so React Fast Refresh keeps working.
 */
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";

/** Shared look for every text-like control so inputs, selects and textareas match */
export const controlClasses = cn(
  "w-full rounded-lg bg-white text-sm text-gray-900 placeholder:text-gray-400",
  "ring-1 ring-inset ring-gray-300 shadow-xs transition-[box-shadow,background-color] duration-150",
  "hover:ring-gray-400",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:shadow-[0_0_0_4px_rgb(249_115_22/0.12)]",
  "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200",
  "aria-[invalid=true]:ring-red-500 aria-[invalid=true]:focus-visible:shadow-[0_0_0_4px_rgb(239_68_68/0.12)]"
);

export const buttonVariants = cva(
  [
    "relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium",
    "transition-[background-color,border-color,color,box-shadow,transform] duration-150",
    "active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        /** Main call to action — FlipIQ orange */
        brand:
          "bg-brand-600 text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.18),0_1px_2px_rgb(194_65_12/0.35)] hover:bg-brand-700 focus-visible:ring-brand-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none disabled:opacity-100",
        /** Strong neutral action */
        primary:
          "bg-gray-900 text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.12),0_1px_2px_rgb(0_0_0/0.2)] hover:bg-gray-800 focus-visible:ring-gray-900",
        secondary:
          "bg-white text-gray-800 ring-1 ring-inset ring-gray-300 shadow-xs hover:bg-gray-50 hover:ring-gray-400 focus-visible:ring-gray-400",
        ghost:
          "text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-gray-400",
        soft:
          "bg-brand-50 text-brand-700 hover:bg-brand-100 focus-visible:ring-brand-400",
        danger:
          "bg-red-600 text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.15)] hover:bg-red-700 focus-visible:ring-red-500",
        link:
          "h-auto px-0 text-brand-700 underline-offset-4 hover:underline focus-visible:ring-brand-400",
        /** Legacy aliases kept so existing call sites keep working */
        cta:
          "bg-brand-600 text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.18),0_1px_2px_rgb(194_65_12/0.35)] hover:bg-brand-700 focus-visible:ring-brand-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none disabled:opacity-100",
        white:
          "bg-white text-gray-900 shadow-sm hover:bg-gray-100 focus-visible:ring-white",
      },
      size: {
        sm: "h-8 px-3 text-meta",
        default: "h-9 px-4 text-sm max-sm:h-11",
        lg: "h-11 px-5 text-title",
        icon: "h-9 w-9 max-sm:h-11 max-sm:w-11",
        "icon-sm": "h-8 w-8",
      },
      block: {
        true: "w-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

export const badgeVariants = cva(
  "inline-flex items-center gap-1 whitespace-nowrap rounded-full font-medium ring-1 ring-inset [&_svg]:size-3",
  {
    variants: {
      tone: {
        neutral: "bg-gray-50 text-gray-700 ring-gray-200",
        brand: "bg-brand-50 text-brand-700 ring-brand-200",
        success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
        warning: "bg-amber-50 text-amber-800 ring-amber-200",
        danger: "bg-red-50 text-red-700 ring-red-200",
        info: "bg-sky-50 text-sky-700 ring-sky-200",
        violet: "bg-violet-50 text-violet-700 ring-violet-200",
        dark: "bg-gray-900 text-white ring-gray-900",
        glass: "bg-white/90 text-gray-900 ring-white/40 shadow-sm backdrop-blur",
        /** Solid orange, for emphasis on photos (e.g. "New") */
        "brand-solid": "bg-brand-600 text-white ring-brand-600",
      },
      size: {
        sm: "h-5 px-2 text-xs",
        md: "h-6 px-2.5 text-xs",
      },
    },
    defaultVariants: { tone: "neutral", size: "md" },
  }
);

export const chipVariants = cva(
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium transition-colors cursor-pointer ring-1 ring-inset",
  {
    variants: {
      variant: {
        default: "bg-white text-gray-700 ring-gray-300 hover:bg-gray-50 hover:ring-gray-400",
        primary: "bg-gray-900 text-white ring-gray-900 hover:bg-gray-800",
      },
      size: {
        md: "h-9 px-3.5 text-sm",
        sm: "h-7 px-3 text-meta",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  }
);

export const alertVariants = cva("flex gap-3 rounded-xl p-4 text-sm ring-1 ring-inset", {
  variants: {
    variant: {
      info: "bg-sky-50 text-sky-900 ring-sky-200",
      success: "bg-emerald-50 text-emerald-900 ring-emerald-200",
      warning: "bg-amber-50 text-amber-900 ring-amber-200",
      error: "bg-red-50 text-red-900 ring-red-200",
    },
  },
  defaultVariants: { variant: "info" },
});
